import React, { useCallback, useState } from "react";
import { Box, FormControl, IconButton, InputLabel, MenuItem, Modal, Select, Stack, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useAuth } from "../../components/TokenHandler";
import UserActionsReport from "./reports/UserActionsReport";
import InboundMessagesReport from "./reports/InboundMessagesReport";
import MessageStatusEventsReport from "./reports/MessageStatusEventsReport";
import ThreeFailedMessagesReport from "./reports/ThreeFailedMessagesReport";
import FacilityGeneralReport from "./reports/FacilityGeneralReport";
import OutgoingMessageTemplatesReport from "./reports/OutgoingMessageTemplatesReport";
import { REPORT_TYPES } from "./reports/reportShared";

const REPORT_DESCRIPTIONS = {
    user_actions: "Review consent activity by action, date range, and employee.",
    inbound_messages: "Review inbound Twilio messages and the associated patient consent information.",
    message_status_events: "Review raw delivered and undelivered Twilio messages with complete status-event history.",
    message_events_three_failed: "Review phone numbers with at least 3 active undelivered messages and cron attempt 3 or higher.",
    facility_general: "Manage facilities using Facility / General consent and add new facilities from the organization search.",
    outgoing_message_templates: "View and update outgoing message templates used by the consent application.",
};

export default function PatientConsentStatsModal({ open, onClose }) {
    const { keycloak: kc } = useAuth();
    const [selectedReportType, setSelectedReportType] = useState("user_actions");
    const apiUrl = String(process.env.REACT_APP_API_URL ?? "").replace(/\/+$/, "");

    const getAccessToken = useCallback(async () => {
        if (!kc) throw new Error("Authentication is not available.");
        if (typeof kc.updateToken === "function") {
            try { await kc.updateToken(30); }
            catch (error) { console.warn("[patient-consent-reports] Token refresh failed:", error); }
        }
        if (!kc.token) throw new Error("Authentication token is unavailable.");
        return kc.token;
    }, [kc]);

    const commonProps = { apiUrl, getAccessToken };

    return (
        <Modal open={open} onClose={onClose} aria-labelledby="patient-consent-report-title">
            <Box sx={{ bgcolor: "background.paper", borderRadius: 2, boxShadow: 24, display: "flex", flexDirection: "column", left: "50%", maxHeight: "99vh", maxWidth: 1500, overflow: "hidden", position: "absolute", top: "50%", transform: "translate(-50%, -50%)", width: { xs: "97%", sm: "95%", md: "92%" } }}>
                <Box sx={{ borderBottom: 1, borderColor: "divider", px: 3, py: 2, pr: 7, position: "relative" }}>
                    <Typography id="patient-consent-report-title" variant="h5" component="h2">Consent Reports</Typography>
                    <Typography color="text.secondary" variant="body2" sx={{ mt: 0.5 }}>{REPORT_DESCRIPTIONS[selectedReportType]}</Typography>
                    <IconButton aria-label="Close reports" onClick={onClose} sx={{ position: "absolute", right: 12, top: 12 }}><CloseIcon /></IconButton>
                </Box>

                <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", p: 3 }}>
                    <Stack spacing={2.5}>
                        <FormControl fullWidth sx={{ maxWidth: 360 }}>
                            <InputLabel id="main-report-type-label">Report</InputLabel>
                            <Select labelId="main-report-type-label" label="Report" value={selectedReportType} onChange={(e) => setSelectedReportType(e.target.value)}>
                                {REPORT_TYPES.map((reportType) => <MenuItem key={reportType.value} value={reportType.value}>{reportType.label}</MenuItem>)}
                            </Select>
                        </FormControl>

                        {selectedReportType === "user_actions" && <UserActionsReport {...commonProps} />}
                        {selectedReportType === "inbound_messages" && <InboundMessagesReport {...commonProps} />}
                        {selectedReportType === "message_status_events" && <MessageStatusEventsReport {...commonProps} />}
                        {selectedReportType === "message_events_three_failed" && <ThreeFailedMessagesReport {...commonProps} keycloak={kc} />}
                        {selectedReportType === "facility_general" && <FacilityGeneralReport {...commonProps} />}
                        {selectedReportType === "outgoing_message_templates" && <OutgoingMessageTemplatesReport {...commonProps} keycloak={kc} />}
                    </Stack>
                </Box>
            </Box>
        </Modal>
    );
}
